import React, { useMemo, useState } from 'react'; // Importamos React y hooks para estado/memoización
import { IonButton, IonContent, IonItem, IonLabel, IonList, IonPage, IonTextarea, IonInput, IonRadioGroup, IonRadio, useIonToast, useIonAlert } from '@ionic/react'; // Controles de Ionic y toasts/alerts para feedback
import Header from 'src/components/Header'; // Header del proyecto para mantener estilos
import Autocomplete from 'src/components/Autocomplete'; // Autocomplete reutilizable para Punto de Servicio
import Autocompleteoperario from 'src/components/Autocompleteoperario'; // Autocomplete de Fiscales (por Jefe)
import { calcularPuntajes, Catalogo, getCatalogo, RegistroAcompanamiento, Respuesta } from 'src/services/acompanamientoLocal'; // Reutilizamos tipos y cálculo
import AcompanamientoAPI from 'src/services/acompanamiento'; // Nuevo servicio a backend
import PuntoServicioAPI from 'src/services/puntoDeServicio'; // Servicio para obtener datos del Punto de Servicio (incluye fiscal por PS)

// Props esperadas para el formulario
type Props = { // Definimos las props necesarias
  user: string; // Nombre del usuario (Fiscal)
};

// Utilidad: generar un ID legible basado en fecha/hora
const generarId = (): string => { // Creamos un identificador sencillo
  const d = new Date(); // Fecha actual
  const pad = (n: number) => String(n).padStart(2, '0'); // Helper para ceros
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`; // YYYY-MM-DD-HHMMSS
}; // Fin generarId

// Estructura compacta de pregunta para esta vista
type Pregunta = { // Tipo de pregunta de cuestionario
  id: string; // Código único
  texto: string; // Enunciado visible
  tipo: 'SNN' | 'RATING'; // Tipo de respuesta (Si/No/No aplica) o calificación final
  orden: number; // Orden de presentación
};

// Preguntas del cuestionario (una sola sección)
const PREGUNTAS: Pregunta[] = [ // Lista con orden y tipo
  { id: 'q01_comunica_gestiona_ausencias', texto: '¿Comunica, gestiona las ausencias?', tipo: 'SNN', orden: 1 },
  { id: 'q06_marca_entrada_salida_ps', texto: '¿Marca entrada y salida en el PS?', tipo: 'SNN', orden: 6 },
  { id: 'q07_revisa_oml', texto: '¿Revisa OML?', tipo: 'SNN', orden: 7 },
  { id: 'q08_controla_dilusiones_identificados', texto: '¿Controla las diluciones de los productos y garantiza que estén identificados?', tipo: 'SNN', orden: 8 },
  { id: 'q09_verifica_elementos_maquinas', texto: '¿Verifica elementos y máquinas? ¿Solicita reparación o cambio?', tipo: 'SNN', orden: 9 },
  { id: 'q10_rutina_actualizada_baltics', texto: '¿La rutina está actualizada según los Baltics vigentes y necesidades reales del PS?', tipo: 'SNN', orden: 10 },
  { id: 'q11_acompana_rutina', texto: '¿Acompaña la rutina de los operarios?', tipo: 'SNN', orden: 11 },
  { id: 'q12_verifica_limpieza_procedimientos', texto: '¿Verifica que los operarios realicen la limpieza de acuerdo a los procedimientos establecidos?', tipo: 'SNN', orden: 12 },
  { id: 'q13_inspecciona_limpieza_ps', texto: '¿Inspecciona limpieza en el PS?', tipo: 'SNN', orden: 13 },
  { id: 'q14_gestiona_detalles_en_rutina', texto: '¿Gestiona los detalles encontrados en el momento y garantiza que estén en la rutina?', tipo: 'SNN', orden: 14 },
  { id: 'q15_verifica_cronograma_especificos', texto: '¿Verifica Cronograma de lavados profundos, limpiezas de vidrios en altura y servicios específicos?', tipo: 'SNN', orden: 15 },
  { id: 'q16_genera_inc_ot', texto: '¿Genera INC/OT?', tipo: 'SNN', orden: 16 },
  { id: 'q17_programa_especificos', texto: '¿Programa los lavados profundos, limpieza de vidrios en altura y servicios específicos?', tipo: 'SNN', orden: 17 },
  { id: 'q18_solicita_traslado_maquinas', texto: '¿Solicita traslado de máquinas?', tipo: 'SNN', orden: 18 },
  { id: 'q19_revisa_planilla_epis', texto: '¿Revisa planilla de entrega de EPIS?', tipo: 'SNN', orden: 19 },
  { id: 'q20_carga_inventario_productos', texto: '¿Carga el inventario de productos?', tipo: 'SNN', orden: 20 },
  { id: 'q21_completa_informe_fiscalizacion', texto: '¿Completa informe de fiscalización?', tipo: 'SNN', orden: 21 },
  { id: 'q22_solicita_capacitacion', texto: '¿Solicita capacitación?', tipo: 'SNN', orden: 22 },
  { id: 'q23_solicita_aplica_sanciones', texto: '¿Solicita sanciones? ¿Aplica sanciones?', tipo: 'SNN', orden: 23 },
  { id: 'q24_controla_imagen_colaboradores', texto: '¿Controla política de imagen de los colaboradores?', tipo: 'SNN', orden: 24 },
  { id: 'q25_gestiona_rrhh_uniformes', texto: '¿Gestiona con RRHH la provisión de los uniformes en general?', tipo: 'SNN', orden: 25 },
  { id: 'q26_asignaciones_app', texto: '¿Realiza asignaciones por la APP?', tipo: 'SNN', orden: 26 },
  { id: 'q27_charla_bajar_directivas', texto: '¿En su visita realiza charla con su equipo para bajar las directivas?', tipo: 'SNN', orden: 27 },
  { id: 'q28_pedidos_ingreso_tiempo_forma', texto: '¿Gestiona los pedidos de ingreso en tiempo y en forma en los punto de servicios?', tipo: 'SNN', orden: 28 },
  { id: 'q29_relacionamiento_cliente_externo', texto: '¿Mantiene un buen relacionamiento con el cliente externo?', tipo: 'SNN', orden: 29 },
  { id: 'q30_relacionamiento_cliente_interno', texto: '¿Mantiene un buen relacionamiento con el cliente interno?', tipo: 'SNN', orden: 30 },
  { id: 'q31_proactividad_soluciones', texto: '¿Demuestra proactividad y capacidad para solucionar los problemas?', tipo: 'SNN', orden: 31 },
  { id: 'q32_calificacion_gestion', texto: '¿Cómo calificaría la gestión del fiscalizador?', tipo: 'RATING', orden: 32 },
];

// Opciones estándar
const OPCIONES_SNN = [ // Si/No/No aplica
  { etiqueta: 'Si', valor: 1 }, // Aporta puntaje completo
  { etiqueta: 'No', valor: 0 }, // Cero puntaje
  { etiqueta: 'No aplica', valor: -1 }, // Se ignora del denominador
];
const OPCIONES_RATING = [ // Calificación final
  { etiqueta: 'Excelente', valor: 1 },
  { etiqueta: 'Muy buena', valor: 0.8 },
  { etiqueta: 'Buena', valor: 0.6 },
  { etiqueta: 'Regular', valor: 0.4 },
  { etiqueta: 'Deficiente', valor: 0.2 },
];

// Componente del formulario de Acompañamiento
const AcompanamientoForm: React.FC<Props> = ({ user }) => { // Declaramos el componente
  const catalogo: Catalogo = useMemo(() => getCatalogo(), []); // Conservamos para versionado

  const [fiscal, setFiscal] = useState<{ id: string; usuario: string; cargo: string } | null>(null); // Fiscal a evaluar con id
  const [ps, setPs] = useState<{ id: string; nombre: string } | null>(null); // PS seleccionado
  const [numeroAcomp, setNumeroAcomp] = useState<string>(() => generarId()); // Nº de Acompañamiento
  const [present] = useIonToast(); // Hook para mostrar toasts de feedback
  const [presentAlert] = useIonAlert(); // Hook para confirmaciones (alert)

  // Helper simple para permitir que el toast se vea antes de navegar
  const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

  // Comentarios generales y plan de acción (sustituyen comentarios por pregunta)
  const [comentarios, setComentarios] = useState<string>(''); // Comentarios generales
  const [planAccion, setPlanAccion] = useState<string>(''); // Plan de acción

  // Inicializamos respuestas en base a PREGUNTAS
  const [respuestas, setRespuestas] = useState<Respuesta[]>(() => { // Respuestas iniciales
    return PREGUNTAS.map((p, idx) => ({ // Mapeamos cada pregunta
      bloque: 'VISITA', // Usamos un único bloque para el cálculo
      codigo: p.id, // Código de la pregunta
      valor: null, // Sin selección inicial para obligar a marcar
      peso: 1, // Peso uniforme
      orden: p.orden ?? idx + 1, // Orden
      visitaOrden: 1, // Visita 1
    })); // Fin map
  }); // Fin useState

  // Conjunto de preguntas faltantes para resaltar en UI cuando validamos al enviar
  const [faltantesSet, setFaltantesSet] = useState<Set<string>>(() => new Set()); // Codigos de preguntas sin responder

  const setValor = (codigo: string, valor: number) => { // Actualiza una respuesta por código
    setRespuestas(prev => prev.map(r => (r.codigo === codigo ? { ...r, valor } : r))); // Reemplazamos valor
    // Si la pregunta estaba marcada como faltante, la quitamos del conjunto al responder
    setFaltantesSet(prev => {
      if (!prev.has(codigo)) return prev;
      const nuevo = new Set(prev);
      nuevo.delete(codigo);
      return nuevo;
    });
  }; // Fin setValor

  // Buscar y autocompletar el Fiscal a partir del PS elegido (por ps_codigo/id del Autocomplete)
  const fetchFiscalPorPs = async (psCodigo: string) => { // Encapsula la llamada al backend
    try { // Bloque de control de errores
      const { data } = await PuntoServicioAPI.obtenerFiscalPorPs(psCodigo); // Llamamos al endpoint nuevo
      const f = data?.datos; // Extraemos datos del fiscal
      if (f && f.id) { // Si existe un fiscal asociado
        setFiscal({ id: String(f.id), usuario: String(f.nombre || ''), cargo: 'FISCALIZADOR A' }); // Seteamos fiscal autocompletado y normalizado
      } else {
        setFiscal(null); // Si no existe, permitimos selección manual
      }
    } catch { // Ante excepciones o errores HTTP
      setFiscal(null); // No bloqueamos: permitimos selección manual
    }
  }; // Fin fetchFiscalPorPs

  // Cálculo de puntajes: ignoramos No aplica (valor -1) del denominador
  const puntajes = useMemo(() => { // Memoizamos
    const filtradas = respuestas.map(r => (r.valor !== null && r.valor < 0 ? { ...r, peso: 0, valor: 0 } : r)); // Las NA no suman peso
    return calcularPuntajes(filtradas); // Reutilizamos la función existente
  }, [respuestas]); // Recalcula ante cambios

  const onGuardar = async () => { // Guardar registro (acción efectiva)
    if (!ps) { // Validamos PS primero
      present({ message: 'Selecciona un Punto de Servicio para la visita.', duration: 3000, color: 'warning' }); // Toast informativo
      return; // Cortamos flujo
    } // Fin validación PS
    if (!fiscal) { // Validamos Fiscal
      present({ message: 'Selecciona un Fiscal para la visita.', duration: 3000, color: 'warning' }); // Toast informativo
      return; // Cortamos flujo
    } // Fin validación Fiscal

    // Validación: todas las preguntas deben estar respondidas (valor distinto de null)
    const faltantes = respuestas.filter(r => r.valor === null).map(r => r.codigo); // Obtenemos códigos faltantes
    if (faltantes.length > 0) { // Si hay preguntas pendientes
      setFaltantesSet(new Set(faltantes)); // Guardamos para resaltar en UI
      present({ message: `Debes completar todas las preguntas del cuestionario. Faltan ${faltantes.length}.`, duration: 4000, color: 'danger' }); // Toast de error
      return; // Evitamos continuar
    }

    const ahoraIso = new Date().toISOString(); // Fecha actual

    // Construimos payload para backend
    const payload = {
      numero: numeroAcomp,
      catalogoVersion: catalogo.version,
      ps: { codigo: String(ps.id), nombre: ps.nombre },
      fiscal: { id: fiscal.id, nombre: fiscal.usuario },
      fechaVisitaIso: ahoraIso,
      respuestas,
      puntajes: { total: puntajes.total, porBloque: puntajes.porBloque },
      comentarios,
      planAccion,
    };

    try {
      const { data, status } = await AcompanamientoAPI.crear(payload); // POST backend
      if (status >= 200 && status < 300 && data?.success) {
        const id = data?.datos?.id; // Id generado por backend
        const msg = id ? `Acompañamiento guardado correctamente. ID: ${id}` : 'Acompañamiento guardado correctamente.';
        present({ message: msg, duration: 2500, color: 'success' }); // Toast éxito
        await sleep(900); // Permitimos que el usuario vea el mensaje
        window.location.href = '/acompanamiento'; // Redirigimos al índice
      } else {
        present({ message: data?.error || 'No se pudo guardar el acompañamiento.', duration: 3500, color: 'danger' });
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Error de conexión al guardar el acompañamiento.';
      present({ message: msg, duration: 4000, color: 'danger' });
    }
  }; // Fin onGuardar

  const onConfirmarEnvio = async () => {
    // Confirmación previa al guardado: evita envíos accidentales.
    // Nota: las validaciones finales también se ejecutan dentro de onGuardar.
    presentAlert({
      header: 'Confirmar guardado',
      message: '¿Deseas guardar este acompañamiento?',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Guardar',
          handler: () => {
            void onGuardar();
          },
        },
      ],
    });
  };

  // Render
  return (
    <IonPage>
      <Header titulo="Acompañamiento" />
      <IonContent id="acompanamiento-form" className="ion-padding">
        <div className="container mt-1">
          {/* Encabezado: Fiscal, Fecha, Nº y PS */}
          <div className="card" style={{ padding: 12 }}>
            <IonItem lines="none">
              <IonLabel position="stacked">Fiscal</IonLabel>
              <IonInput readonly value={user} />
            </IonItem>
            <IonItem lines="none">
              <IonLabel position="stacked">Fecha (automática)</IonLabel>
              <IonInput readonly value={new Date().toLocaleString()} />
            </IonItem>
            <IonItem lines="none">
              <IonLabel position="stacked">Nº de Acompañamiento</IonLabel>
              <IonInput value={numeroAcomp} onIonChange={(e) => setNumeroAcomp(String(e.detail.value))} />
            </IonItem>
            <div className="mt-1" style={{ maxWidth: 480 }}>
              <Autocomplete // Primero elegimos el Punto de Servicio
                label="Punto de Servicio"
                value={ps}
                setValue={(op) => { // Al elegir PS
                  setPs(op); // Seteamos el PS
                  setFiscal(null); // Limpiamos fiscal previo
                  if (op && op.id) { // Si hay ps_codigo disponible
                    fetchFiscalPorPs(String(op.id)); // Autocompletamos el fiscal del PS
                  }
                }}
                disabled={false} // Siempre habilitado: PS se elige primero
              />
            </div>
            <div className="mt-1" style={{ maxWidth: 480 }}>
              <Autocompleteoperario // Fiscal dependiente del PS (autocompletado si existe, manual solo si no existe)
                label="Fiscal a evaluar"
                value={fiscal}
                setValue={(op) => { setFiscal(op); }} // Permitimos setear solo si está habilitado (cuando no hay fiscal asignado)
                disabled={!ps || !!fiscal} // Deshabilitado hasta elegir PS; si hay fiscal autocompletado, permanece deshabilitado
              />
            </div>
          </div>

          {/* Cuestionario */}
          <div className="card mt-2" style={{ padding: 12 }}>
            <h6>Cuestionario</h6>
            <IonList>
              {PREGUNTAS.sort((a, b) => a.orden - b.orden).map((p) => {
                // Obtenemos la respuesta actual
                const r = respuestas.find(x => x.codigo === p.id);
                const valor = r?.valor; // null al inicio (sin selección)
                const esFaltante = faltantesSet.has(p.id); // Determina si la pregunta está faltante para resaltar
                // Opciones según tipo
                const opciones = p.tipo === 'SNN' ? OPCIONES_SNN : OPCIONES_RATING;
                return (
                  <div key={p.id} className="mb-2" style={{ border: esFaltante ? '1px solid #dc3545' : '1px solid rgba(0,0,0,0.08)', borderRadius: 10, padding: 10 }}>
                    <IonItem lines="none">
                      <IonLabel className="ion-text-wrap"><strong>{p.texto}</strong>{esFaltante ? ' *' : ''}</IonLabel>
                    </IonItem>
                    <IonRadioGroup value={valor === null ? undefined : String(valor)} onIonChange={(e) => setValor(p.id, Number(e.detail.value))}> {/* Grupo sin selección por defecto */}
                      {opciones.map(opt => (
                        <IonItem key={opt.etiqueta} lines="none">
                          <IonRadio slot="start" value={String(opt.valor)} />
                          <IonLabel>{opt.etiqueta}</IonLabel>
                        </IonItem>
                      ))}
                    </IonRadioGroup>
                    {esFaltante ? <small style={{ color: '#dc3545' }}>Requerido</small> : null}
                  </div>
                );
              })}
            </IonList>
          </div>

          {/* Comentarios generales y Plan de acción */}
          <div className="card mt-2" style={{ padding: 12 }}>
            <IonItem lines="none">
              <IonLabel position="stacked">Comentarios</IonLabel>
              <IonTextarea value={comentarios} onIonChange={(e) => setComentarios(String(e.detail.value))} autoGrow rows={3} style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, padding: 8 }} />
            </IonItem>
            <IonItem lines="none" className="mt-2">
              <IonLabel position="stacked">Plan de acción</IonLabel>
              <IonTextarea value={planAccion} onIonChange={(e) => setPlanAccion(String(e.detail.value))} autoGrow rows={3} style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, padding: 8 }} />
            </IonItem>
          </div>

          {/* Footer con acciones y puntajes */}
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <small className="text-muted">Cuestionario: {puntajes.porBloque['VISITA#1'] ?? 0}% · Total: <strong>{puntajes.total}%</strong></small>
            </div>
            <div>
              <IonButton color="medium" onClick={() => (window.location.href = '/acompanamiento')}>Atrás</IonButton>
              <IonButton color="primary" onClick={onConfirmarEnvio} className="ml-1">Enviar</IonButton>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AcompanamientoForm; // Exportamos el formulario